package songwritersnotebook.app.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.data.RoadmapItem
import songwritersnotebook.app.data.RoadmapItemType
import songwritersnotebook.app.data.RoadmapStatus
import songwritersnotebook.app.viewmodels.RoadmapViewModel
import songwritersnotebook.app.viewmodels.RoadmapUiState
import songwritersnotebook.app.getPlatform
import songwritersnotebook.app.components.UserAccountHeader
import songwritersnotebook.app.components.WebNavHeader
import songwritersnotebook.app.components.WebFooter
import songwritersnotebook.app.viewmodels.MainViewModel
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun RoadmapScreen(
    onBack: () -> Unit,
    onFeatures: () -> Unit,
    onPricing: () -> Unit,
    onRoadmap: () -> Unit,
    onHome: () -> Unit,
    onPrivacy: () -> Unit = {},
    onTerms: () -> Unit = {},
    onOpenUrl: (String) -> Unit = {},
    viewModel: RoadmapViewModel,
    isAdmin: Boolean = false,
    mainViewModel: MainViewModel,
    onSignIn: () -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()
    val isWeb = remember { getPlatform().name.contains("Web", ignoreCase = true) }
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    
    LaunchedEffect(isAdmin) {
        viewModel.setAdminStatus(isAdmin)
    }

    LaunchedEffect(uiState.error) {
        uiState.error?.let {
            snackbarHostState.showSnackbar(it)
        }
    }

    Scaffold(
        topBar = {
            if (isWeb) {
                WebNavHeader(
                    mainViewModel = mainViewModel,
                    onSignIn = onSignIn,
                    onFeatures = onFeatures,
                    onPricing = onPricing,
                    onRoadmap = onRoadmap,
                    onHome = onHome,
                    selectedRoute = "roadmap"
                )
            } else {
                Box(modifier = Modifier.windowInsetsPadding(WindowInsets.statusBars)) {
                    TopAppBar(
                        title = { 
                            Column {
                                Text("Product Roadmap", fontWeight = FontWeight.Bold)
                                if (uiState.isAdmin) {
                                    Text("Admin Mode Active", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        },
                        navigationIcon = {
                            IconButton(onClick = onBack) {
                                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                            }
                        },
                        actions = {
                            if (uiState.isAdmin) {
                                IconButton(onClick = { viewModel.toggleAddDialog(true) }) {
                                    Icon(Icons.Default.Add, contentDescription = "Add Item")
                                }
                            }
                            // Space for account menu
                            Spacer(modifier = Modifier.width(64.dp))
                        }
                    )
                    UserAccountHeader(mainViewModel = mainViewModel, onSignIn = onSignIn)
                }
            }
        },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        floatingActionButton = {
            if (isWeb && uiState.currentUserId != null) {
                ExtendedFloatingActionButton(
                    onClick = { viewModel.toggleAddDialog(true) },
                    icon = { Icon(Icons.Default.Add, null) },
                    text = { Text("Add Item") },
                    containerColor = MaterialTheme.colorScheme.primary
                )
            }
        }
    ) { paddingValues ->
        val verticalScrollState = rememberScrollState()
        BoxWithConstraints(modifier = Modifier.padding(paddingValues).fillMaxSize()) {
            val minHeight = maxHeight
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(verticalScrollState)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().heightIn(min = minHeight)
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = if (isWeb) 24.dp else 0.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Box(modifier = Modifier.fillMaxWidth(if (isWeb) 0.9f else 1f)) {
                            if (uiState.isLoading) {
                                Box(modifier = Modifier.fillMaxWidth().height(400.dp), contentAlignment = Alignment.Center) {
                                    CircularProgressIndicator()
                                }
                            } else {
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        OutlinedTextField(
                                            value = uiState.searchQuery,
                                            onValueChange = { viewModel.setSearchQuery(it) },
                                            placeholder = { Text("Search suggestions...") },
                                            leadingIcon = { Icon(Icons.Default.Search, null) },
                                            modifier = Modifier.weight(1f)
                                        )
                                        Spacer(modifier = Modifier.width(16.dp))
                                        var sortExpanded by remember { mutableStateOf(false) }
                                        Box {
                                            OutlinedButton(onClick = { sortExpanded = true }) {
                                                Text("Sort: ${uiState.sortBy.name}")
                                                Icon(Icons.Default.ArrowDropDown, null)
                                            }
                                            DropdownMenu(expanded = sortExpanded, onDismissRequest = { sortExpanded = false }) {
                                                songwritersnotebook.app.viewmodels.RoadmapSortBy.entries.forEach { sortBy ->
                                                    DropdownMenuItem(
                                                        text = { Text(sortBy.name) },
                                                        onClick = { viewModel.setSortBy(sortBy); sortExpanded = false }
                                                    )
                                                }
                                            }
                                        }
                                    }

                                    val filteredItems = uiState.items.filter { 
                                        it.title.contains(uiState.searchQuery, ignoreCase = true) || 
                                        it.description.contains(uiState.searchQuery, ignoreCase = true) 
                                    }.let { list ->
                                        if (uiState.sortBy == songwritersnotebook.app.viewmodels.RoadmapSortBy.TOP_VOTED) {
                                            list.sortedByDescending { it.voteCount }
                                        } else {
                                            list.sortedByDescending { it.createdAt }
                                        }
                                    }

                                    val suggested = filteredItems.filter { uiState.currentUserId != null && it.authors.contains(uiState.currentUserId) }
                                    val approved = filteredItems.filter { it.status == RoadmapStatus.APPROVED }
                                    val inProgress = filteredItems.filter { it.status == RoadmapStatus.IN_PROGRESS }
                                    val onHold = filteredItems.filter { it.status == RoadmapStatus.ON_HOLD }
                                    val completed = filteredItems.filter { it.status == RoadmapStatus.COMPLETED }
                                if (isWeb) {
                                    // Desktop Layout: Horizontal Scrollable Columns
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .horizontalScroll(rememberScrollState())
                                            .padding(vertical = 32.dp),
                                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                                    ) {
                                        RoadmapColumn("My Contributions", suggested, Modifier.width(300.dp), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("Open for Voting", approved, Modifier.width(300.dp), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("In Progress", inProgress, Modifier.width(300.dp), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("On Hold", onHold, Modifier.width(300.dp), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("Completed", completed, Modifier.width(300.dp), viewModel, uiState, scope, snackbarHostState)
                                    }
                                } else {
                                    // Mobile Layout: Vertical Stack
                                    Column(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(16.dp),
                                        verticalArrangement = Arrangement.spacedBy(24.dp)
                                    ) {
                                        RoadmapColumn("My Contributions", suggested, Modifier.fillMaxWidth(), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("Open for Voting", approved, Modifier.fillMaxWidth(), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("In Progress", inProgress, Modifier.fillMaxWidth(), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("On Hold", onHold, Modifier.fillMaxWidth(), viewModel, uiState, scope, snackbarHostState)
                                        RoadmapColumn("Completed", completed, Modifier.fillMaxWidth(), viewModel, uiState, scope, snackbarHostState)
                                    }
                                    }
                                }
                            }

                            if (uiState.showAddDialog) {
                                AddRoadmapItemDialog(
                                    onDismiss = { viewModel.toggleAddDialog(false) },
                                    onConfirm = { title, desc, type -> viewModel.addItem(title, desc, type) }
                                )
                            }
                        }
                    }
                    
                    Spacer(modifier = Modifier.height(80.dp))
                }

                if (isWeb) {
                    WebFooter(
                        onNavigateToHome = onHome,
                        onNavigateToFeatures = onFeatures,
                        onNavigateToPricing = onPricing,
                        onNavigateToRoadmap = onRoadmap,
                        onNavigateToPrivacy = onPrivacy,
                        onNavigateToTerms = onTerms,
                        onOpenUrl = onOpenUrl,
                        isCompact = false
                    )
                }
            }
        }
    }
}

@Composable
fun RoadmapColumn(
    title: String,
    items: List<RoadmapItem>,
    modifier: Modifier,
    viewModel: RoadmapViewModel,
    uiState: RoadmapUiState,
    scope: kotlinx.coroutines.CoroutineScope,
    snackbarHostState: SnackbarHostState
) {
    Column(modifier = modifier) {
        Surface(
            color = MaterialTheme.colorScheme.primaryContainer,
            shape = RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(
                text = "$title (${items.size})",
                modifier = Modifier.padding(12.dp),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onPrimaryContainer
            )
        }
        
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
                .border(1.dp, MaterialTheme.colorScheme.outlineVariant)
                .padding(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            if (items.isEmpty()) {
                Text(
                    "No items yet.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(16.dp).fillMaxWidth(),
                    textAlign = TextAlign.Center
                )
            }
            items.forEach { item ->
                RoadmapItemCard(item, viewModel, uiState, scope, snackbarHostState)
            }
        }
    }
}

@Composable
fun RoadmapItemCard(
    item: RoadmapItem,
    viewModel: RoadmapViewModel,
    uiState: RoadmapUiState,
    scope: kotlinx.coroutines.CoroutineScope,
    snackbarHostState: SnackbarHostState
) {
    val userVote = uiState.userVotes[item.id] ?: 0
    val isLoggedIn = uiState.currentUserId != null

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                val typeColor = when (item.type) {
                    RoadmapItemType.BUG -> Color(0xFFEF4444)
                    RoadmapItemType.FEATURE -> Color(0xFF3B82F6)
                    RoadmapItemType.IMPROVEMENT -> Color(0xFF10B981)
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Surface(
                        color = typeColor.copy(alpha = 0.1f),
                        shape = RoundedCornerShape(4.dp),
                        border = androidx.compose.foundation.BorderStroke(1.dp, typeColor.copy(alpha = 0.5f))
                    ) {
                        Text(
                            text = item.type.name,
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            style = MaterialTheme.typography.labelSmall,
                            color = typeColor,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    
                    val statusText = when (item.status) {
                        RoadmapStatus.SUGGESTED -> "PENDING APPROVAL"
                        RoadmapStatus.APPROVED -> null
                        else -> item.status.name.replace("_", " ")
                    }
                    
                    if (statusText != null) {
                        val statusColor = if (item.status == RoadmapStatus.SUGGESTED) MaterialTheme.colorScheme.outline else MaterialTheme.colorScheme.secondary
                        Surface(
                            color = statusColor.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(4.dp),
                            border = androidx.compose.foundation.BorderStroke(1.dp, statusColor.copy(alpha = 0.5f))
                        ) {
                            Text(
                                text = statusText,
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = statusColor,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                if (uiState.isAdmin) {
                    var showEditDialog by remember { mutableStateOf(false) }
                    if (showEditDialog) {
                        EditRoadmapItemDialog(
                            item = item,
                            onDismiss = { showEditDialog = false },
                            onConfirm = { t, d, ty -> 
                                viewModel.updateItemDetails(item, t, d, ty)
                                showEditDialog = false
                            }
                        )
                    }

                    Row {
                        var showStatusMenu by remember { mutableStateOf(false) }
                        var showMergeMenu by remember { mutableStateOf(false) }
                        IconButton(onClick = { showEditDialog = true }) {
                            Icon(Icons.Default.Edit, "Edit", modifier = Modifier.size(18.dp))
                        }
                        IconButton(onClick = { showStatusMenu = true }) {
                            Icon(Icons.Default.SyncAlt, "Change Status", modifier = Modifier.size(18.dp))
                            DropdownMenu(expanded = showStatusMenu, onDismissRequest = { showStatusMenu = false }) {
                                RoadmapStatus.entries.forEach { status ->
                                    DropdownMenuItem(
                                        text = { Text(status.name) },
                                        onClick = {
                                            viewModel.updateStatus(item, status)
                                            showStatusMenu = false
                                        }
                                    )
                                }
                            }
                        }
                        IconButton(onClick = { showMergeMenu = true }) {
                            Icon(Icons.Default.CallMerge, "Merge", modifier = Modifier.size(18.dp))
                            DropdownMenu(expanded = showMergeMenu, onDismissRequest = { showMergeMenu = false }) {
                                val otherItems = uiState.items.filter { it.id != item.id }
                                if (otherItems.isEmpty()) {
                                    DropdownMenuItem(text = { Text("No other items") }, onClick = {})
                                } else {
                                    otherItems.forEach { target ->
                                        DropdownMenuItem(
                                            text = { Text("Merge into: " + target.title.take(20) + if (target.title.length > 20) "..." else "") },
                                            onClick = {
                                                viewModel.mergeItem(item, target.id)
                                                showMergeMenu = false
                                            }
                                        )
                                    }
                                }
                            }
                        }
                        IconButton(onClick = { viewModel.deleteItem(item) }) {
                            Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(18.dp))
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(8.dp))
            Text(text = item.title, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = item.description,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Spacer(modifier = Modifier.height(12.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.End
            ) {
                // UPVOTES
                Text(
                    text = item.upvotes.toString(),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = if (userVote == 1) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                )
                IconButton(
                    onClick = { 
                        if (isLoggedIn) {
                            viewModel.vote(item, 1)
                        } else {
                            scope.launch { snackbarHostState.showSnackbar("Please sign in to vote.") }
                        }
                    },
                    modifier = Modifier.size(32.dp)
                ) {
                    Icon(
                        Icons.Default.ArrowUpward, 
                        "Upvote", 
                        tint = if (userVote == 1) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f),
                        modifier = Modifier.size(20.dp)
                    )
                }
                
                Spacer(modifier = Modifier.width(8.dp))

                // DOWNVOTES
                Text(
                    text = item.downvotes.toString(),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = if (userVote == -1) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurfaceVariant
                )
                IconButton(
                    onClick = { 
                        if (isLoggedIn) {
                            viewModel.vote(item, -1)
                        } else {
                            scope.launch { snackbarHostState.showSnackbar("Please sign in to vote.") }
                        }
                    },
                    modifier = Modifier.size(32.dp)
                ) {
                    Icon(
                        Icons.Default.ArrowDownward, 
                        "Downvote", 
                        tint = if (userVote == -1) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f),
                        modifier = Modifier.size(20.dp)
                    )
                }
            }
            
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            var commentsExpanded by remember { mutableStateOf(false) }
            Row(
                modifier = Modifier.fillMaxWidth().clickable { 
                    commentsExpanded = !commentsExpanded
                    if (commentsExpanded && !uiState.comments.containsKey(item.id)) {
                        viewModel.loadComments(item.id)
                    }
                }.padding(vertical = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(if (commentsExpanded) "Hide Comments" else "View Comments", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary)
                Icon(if (commentsExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore, null, tint = MaterialTheme.colorScheme.primary)
            }
            if (commentsExpanded) {
                val comments = uiState.comments[item.id] ?: emptyList()
                Column(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (comments.isEmpty()) {
                        Text("No comments yet.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                    comments.forEach { comment ->
                        Surface(color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f), shape = RoundedCornerShape(8.dp)) {
                            Column(modifier = Modifier.padding(8.dp).fillMaxWidth()) {
                                Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                                    Text(getRelativeTimeString(comment.createdAt), style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                    if (uiState.isAdmin || comment.authorId == uiState.currentUserId) {
                                        IconButton(onClick = { viewModel.deleteComment(item.id, comment.id) }, modifier = Modifier.size(16.dp)) {
                                            Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(12.dp))
                                        }
                                    }
                                }
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(comment.text, style = MaterialTheme.typography.bodySmall)
                            }
                        }
                    }
                    
                    var newCommentText by remember { mutableStateOf("") }
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                        OutlinedTextField(
                            value = newCommentText,
                            onValueChange = { newCommentText = it },
                            placeholder = { Text("Add a comment...") },
                            modifier = Modifier.weight(1f),
                            textStyle = MaterialTheme.typography.bodySmall,
                            singleLine = true
                        )
                        IconButton(onClick = { 
                            if (newCommentText.isNotBlank()) {
                                viewModel.addComment(item.id, newCommentText)
                                newCommentText = ""
                            }
                        }, enabled = newCommentText.isNotBlank()) {
                            Icon(Icons.Default.Send, "Send", tint = MaterialTheme.colorScheme.primary)
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun AddRoadmapItemDialog(
    onDismiss: () -> Unit,
    onConfirm: (String, String, RoadmapItemType) -> Unit
) {
    var title by remember { mutableStateOf("") }
    var desc by remember { mutableStateOf("") }
    var type by remember { mutableStateOf(RoadmapItemType.FEATURE) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Add Roadmap Item") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(value = title, onValueChange = { if (it.length <= 100) title = it }, label = { Text("Title") }, modifier = Modifier.fillMaxWidth(), supportingText = { Text("${title.length}/100") })
                OutlinedTextField(value = desc, onValueChange = { if (it.length <= 1000) desc = it }, label = { Text("Description") }, modifier = Modifier.fillMaxWidth(), minLines = 3, supportingText = { Text("${desc.length}/1000") })
                
                Text("Type", style = MaterialTheme.typography.labelLarge)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    RoadmapItemType.entries.forEach { t ->
                        FilterChip(
                            selected = type == t,
                            onClick = { type = t },
                            label = { Text(t.name) }
                        )
                    }
                }
            }
        },
        confirmButton = {
            Button(onClick = { onConfirm(title, desc, type) }, enabled = title.isNotBlank() && desc.isNotBlank()) { Text("Add") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        }
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun EditRoadmapItemDialog(
    item: RoadmapItem,
    onDismiss: () -> Unit,
    onConfirm: (String, String, RoadmapItemType) -> Unit
) {
    var title by remember { mutableStateOf(item.title) }
    var desc by remember { mutableStateOf(item.description) }
    var type by remember { mutableStateOf(item.type) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Edit Roadmap Item") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(value = title, onValueChange = { if (it.length <= 100) title = it }, label = { Text("Title") }, modifier = Modifier.fillMaxWidth(), supportingText = { Text("${title.length}/100") })
                OutlinedTextField(value = desc, onValueChange = { if (it.length <= 1000) desc = it }, label = { Text("Description") }, modifier = Modifier.fillMaxWidth(), minLines = 3, supportingText = { Text("${desc.length}/1000") })
                
                Text("Type", style = MaterialTheme.typography.labelLarge)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    RoadmapItemType.entries.forEach { t ->
                        FilterChip(
                            selected = type == t,
                            onClick = { type = t },
                            label = { Text(t.name) }
                        )
                    }
                }
            }
        },
        confirmButton = {
            Button(onClick = { onConfirm(title, desc, type) }, enabled = title.isNotBlank() && desc.isNotBlank()) { Text("Save") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        }
    )
}

fun getRelativeTimeString(epochMillis: Long): String {
    if (epochMillis == 0L) return ""
    val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    val diff = now - epochMillis
    val seconds = diff / 1000
    val minutes = seconds / 60
    val hours = minutes / 60
    val days = hours / 24
    return when {
        days > 0 -> "$days days ago"
        hours > 0 -> "$hours hours ago"
        minutes > 0 -> "$minutes minutes ago"
        else -> "just now"
    }
}
